import React, { useState } from "react";
import { Link } from "react-router-dom";
import adminApi from "../../api/adminApi";
import "./Admin.css";

const emptyQuestion = () => ({
  questionText: "",
  imageUrl: "",
  imagePublicId: "",
  imageAlt: "",
  codeSnippet: "",
  codeLanguage: "",
  options: ["", "", "", ""],
  correctAnswer: "",
});

const CODE_LANGUAGES = [
  { value: "", label: "No code" },
  { value: "python", label: "Python" },
  { value: "javascript", label: "JavaScript" },
  { value: "java", label: "Java" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "html", label: "HTML" },
  { value: "css", label: "CSS" },
  { value: "sql", label: "SQL" },
  { value: "json", label: "JSON" },
  { value: "text", label: "Plain Text" },
];

const initialQuizForm = {
  quizId: "",
  title: "",
  description: "",
  level: "Easy",
  timeLimit: 30,
  passMarks: 1,
  technologies: "",
  questions: [emptyQuestion()],
  status: "DRAFT",
};

const quizJsonTemplate = {
  quizId: "js-array-methods-medium",
  title: "JavaScript Array Methods - Medium Exam",
  description: "Medium-level exam covering core JavaScript array methods.",
  timeLimit: 25,
  level: "Medium",
  passMarks: 1,
  technologies: ["JavaScript"],
  questions: [
    {
      questionText: "Which method converts an array to a string?",
      imageUrl: "",
      imagePublicId: "",
      imageAlt: "",
      codeSnippet: "const numbers = [1, 2, 3];\nconsole.log(numbers.join('-'));",
      codeLanguage: "javascript",
      options: ["join()", "split()", "map()", "filter()"],
      correctAnswer: "join()",
    },
  ],
};

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeQuestions = (questions = []) =>
  questions.map((q) => ({
    questionText: String(q.questionText || "").trim(),
    imageUrl: String(q.imageUrl || "").trim(),
    imagePublicId: String(q.imagePublicId || "").trim(),
    imageAlt: String(q.imageAlt || "").trim(),
    codeSnippet: String(q.codeSnippet || ""),
    codeLanguage: String(q.codeLanguage || "").trim(),
    options: Array.isArray(q.options)
      ? q.options.map((o) => String(o || "").trim()).slice(0, 4)
      : ["", "", "", ""],
    correctAnswer: String(q.correctAnswer || "").trim(),
  }));

const parseQuizJson = (text) => {
  const parsed = JSON.parse(text);
  const data = parsed && parsed.quizId ? parsed : parsed?.quiz;
  if (!data || typeof data !== "object") throw new Error("Invalid JSON object.");
  if (!Array.isArray(data.questions) || data.questions.length === 0) {
    throw new Error("JSON must include a non-empty questions array.");
  }

  const questions = normalizeQuestions(data.questions);
  questions.forEach((q, idx) => {
    if (!q.questionText) throw new Error(`Question ${idx + 1}: questionText is required.`);
    if (q.options.length < 2 || q.options.some((item) => !item)) {
      throw new Error(`Question ${idx + 1}: options are required.`);
    }
    if (!q.correctAnswer || !q.options.includes(q.correctAnswer)) {
      throw new Error(`Question ${idx + 1}: correctAnswer must match one option.`);
    }
  });

  return {
    quizId: String(data.quizId || "").trim(),
    title: String(data.title || "").trim(),
    description: String(data.description || "").trim(),
    level: ["Easy", "Medium", "Hard"].includes(data.level) ? data.level : "Easy",
    timeLimit: Number(data.timeLimit || 30),
    passMarks: Number(data.passMarks || 1),
    technologies: Array.isArray(data.technologies) ? data.technologies.join(", ") : "",
    status: ["DRAFT", "PUBLISHED"].includes(data.status) ? data.status : "DRAFT",
    questions,
  };
};

const AdminQuizzes = () => {
  const [form, setForm] = useState(initialQuizForm);
  const [jsonInput, setJsonInput] = useState("");
  const [jsonPreview, setJsonPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const normalizePayload = (source) => ({
    quizId: source.quizId.trim() || slugify(source.title),
    title: source.title.trim(),
    description: source.description.trim(),
    level: source.level,
    timeLimit: Number(source.timeLimit),
    passMarks: Number(source.passMarks),
    technologies: source.technologies
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
    status: source.status,
    questions: source.questions,
  });

  const validatePayload = (payload) => {
    if (!payload.title) throw new Error("Title is required.");
    if (!payload.quizId) throw new Error("Quiz ID is required.");
    if (!payload.questions.length) throw new Error("Add at least one question.");
    if (payload.passMarks > payload.questions.length) {
      throw new Error("Pass marks cannot be greater than total questions.");
    }

    payload.questions.forEach((q, idx) => {
      if (!q.questionText.trim()) throw new Error(`Question ${idx + 1}: question text is required.`);
      const options = q.options.map((item) => String(item || "").trim()).filter(Boolean);
      if (options.length < 2) throw new Error(`Question ${idx + 1}: at least 2 options are required.`);
      if (!q.correctAnswer || !options.includes(q.correctAnswer)) {
        throw new Error(`Question ${idx + 1}: select a valid correct answer.`);
      }
    });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const payload = normalizePayload(form);
      validatePayload(payload);
      await adminApi.createQuiz(payload);
      setSuccess("Exam created successfully. Use Manage Exams to publish, edit, or activate it.");
      setForm(initialQuizForm);
      setJsonInput("");
      setJsonPreview(null);
    } catch (err) {
      setError(err?.message || "Failed to create exam");
    } finally {
      setSaving(false);
    }
  };

  const handleJsonValidate = () => {
    try {
      setError("");
      setSuccess("");
      const parsed = parseQuizJson(jsonInput);
      setJsonPreview(parsed);
      setSuccess(`JSON validated with ${parsed.questions.length} questions.`);
    } catch (err) {
      setJsonPreview(null);
      setError(err?.message || "Invalid JSON format.");
    }
  };

  const handleJsonFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setJsonInput(text);
  };

  const confirmJsonImport = () => {
    if (!jsonPreview) {
      setError("Validate JSON first.");
      return;
    }
    setForm((prev) => ({ ...prev, ...jsonPreview }));
    setSuccess(`Imported ${jsonPreview.questions.length} questions from JSON.`);
  };

  const downloadJsonTemplate = () => {
    const blob = new Blob([JSON.stringify(quizJsonTemplate, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "exam-template.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const addQuestion = () => {
    setForm((prev) => ({ ...prev, questions: [...prev.questions, emptyQuestion()] }));
  };

  const removeQuestion = async (index) => {
    const publicId = form.questions[index]?.imagePublicId;
    if (publicId) {
      try {
        await adminApi.deleteQuestionImage(publicId);
      } catch (err) {
        console.error("Failed to delete removed question image:", err);
      }
    }

    setForm((prev) => {
      if (prev.questions.length <= 1) return prev;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== index) };
    });
  };

  const updateQuestion = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }));
  };

  const uploadQuestionImage = async (qIndex, file) => {
    if (!file) return;
    setError("");
    setUploadingImageIndex(qIndex);

    try {
      const existingPublicId = form.questions[qIndex]?.imagePublicId;
      if (existingPublicId) {
        try {
          await adminApi.deleteQuestionImage(existingPublicId);
        } catch (err) {
          console.error("Failed to delete previous image:", err);
        }
      }

      const formData = new FormData();
      formData.append("image", file);
      const res = await adminApi.uploadQuestionImage(formData);
      updateQuestion(qIndex, {
        imageUrl: res.data.imageUrl,
        imagePublicId: res.data.imagePublicId,
        imageAlt: form.questions[qIndex]?.questionText || file.name,
      });
      setSuccess("Question image uploaded.");
    } catch (err) {
      setError(err?.message || "Failed to upload question image.");
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const removeQuestionImage = async (qIndex) => {
    const publicId = form.questions[qIndex]?.imagePublicId;
    try {
      if (publicId) await adminApi.deleteQuestionImage(publicId);
      updateQuestion(qIndex, { imageUrl: "", imagePublicId: "", imageAlt: "" });
      setSuccess("Question image removed.");
    } catch (err) {
      setError(err?.message || "Failed to remove question image.");
    }
  };

  const resetForm = async () => {
    const imageIds = form.questions.map((q) => q.imagePublicId).filter(Boolean);
    await Promise.all(
      imageIds.map((publicId) =>
        adminApi.deleteQuestionImage(publicId).catch((err) => {
          console.error("Failed to cleanup image:", err);
        })
      )
    );
    setForm(initialQuizForm);
  };

  const updateOption = (qIndex, oIndex, value) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIndex) return q;
        const options = [...q.options];
        options[oIndex] = value;
        const correctAnswer = q.correctAnswer === q.options[oIndex] ? value : q.correctAnswer;
        return { ...q, options, correctAnswer };
      }),
    }));
  };

  return (
    <section className="admin-page">
      <div className="admin-split-head">
        <div>
          <h2>Create Exam</h2>
          <p>Create the exam content here. Publishing and availability are handled from Manage Exams.</p>
        </div>
        <div className="actions-row">
          <Link className="admin-link-button" to="/admin/manage-exams">Manage Exams</Link>
          <Link className="admin-link-button secondary" to="/admin/exam-guide">Exam Guide</Link>
        </div>
      </div>

      <form className="admin-card admin-form" onSubmit={submitForm}>
        <h3>Exam Details</h3>

        <div className="admin-json-block">
          <label htmlFor="quiz-json-input">JSON Import</label>
          <textarea
            id="quiz-json-input"
            rows={7}
            className="admin-json-input"
            placeholder="Paste exam JSON object here"
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
          />
          <div className="actions-row">
            <input type="file" accept="application/json,.json" onChange={handleJsonFile} />
            <button type="button" className="secondary" onClick={handleJsonValidate}>Validate JSON</button>
            <button type="button" className="secondary" onClick={confirmJsonImport}>Confirm Import</button>
            <button type="button" className="secondary" onClick={downloadJsonTemplate}>Download Template</button>
            <span className="admin-json-hint">
              Preview: <strong>{jsonPreview?.questions?.length || 0}</strong> questions
            </span>
          </div>
        </div>

        <div className="admin-form-grid">
          <div className="admin-field-with-btn">
            <input
              type="text"
              placeholder="Exam ID (unique)"
              value={form.quizId}
              onChange={(e) => setForm((prev) => ({ ...prev, quizId: e.target.value }))}
              required
            />
            <button type="button" className="secondary" onClick={() => setForm((p) => ({ ...p, quizId: slugify(p.title) }))}>
              Auto
            </button>
          </div>

          <input
            type="text"
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />

          <select value={form.level} onChange={(e) => setForm((prev) => ({ ...prev, level: e.target.value }))}>
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>

          <input
            type="number"
            min="1"
            placeholder="Time limit (minutes)"
            value={form.timeLimit}
            onChange={(e) => setForm((prev) => ({ ...prev, timeLimit: e.target.value }))}
            required
          />

          <input
            type="number"
            min="1"
            placeholder="Pass marks"
            value={form.passMarks}
            onChange={(e) => setForm((prev) => ({ ...prev, passMarks: e.target.value }))}
            required
          />

          <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
            <option value="DRAFT">Create as Draft</option>
            <option value="PUBLISHED">Create and Publish</option>
          </select>

          <textarea
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            rows={3}
          />

          <input
            type="text"
            placeholder="Technologies (comma separated)"
            value={form.technologies}
            onChange={(e) => setForm((prev) => ({ ...prev, technologies: e.target.value }))}
          />
        </div>

        <div className="admin-question-builder">
          <div className="admin-question-head">
            <h4>Questions</h4>
            <button type="button" className="secondary" onClick={addQuestion}>Add Question</button>
          </div>

          {form.questions.map((question, qIndex) => (
            <div key={`q-${qIndex}`} className="admin-question-card">
              <div className="admin-question-card-head">
                <strong>Question {qIndex + 1}</strong>
                <button type="button" className="danger" onClick={() => removeQuestion(qIndex)}>Delete</button>
              </div>

              <textarea
                rows={2}
                placeholder="Question text"
                value={question.questionText}
                onChange={(e) => updateQuestion(qIndex, { questionText: e.target.value })}
              />

              <div className="question-image-uploader">
                {question.imageUrl ? (
                  <div className="question-image-preview">
                    <img src={question.imageUrl} alt={question.imageAlt || `Question ${qIndex + 1}`} />
                    <button type="button" className="danger" onClick={() => removeQuestionImage(qIndex)}>
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <label className="question-image-drop">
                    <span>
                      {uploadingImageIndex === qIndex ? "Uploading image..." : "Upload image for this question"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={uploadingImageIndex === qIndex}
                      onChange={(e) => uploadQuestionImage(qIndex, e.target.files?.[0])}
                    />
                  </label>
                )}
                {question.imageUrl ? (
                  <input
                    type="text"
                    placeholder="Image alt text"
                    value={question.imageAlt}
                    onChange={(e) => updateQuestion(qIndex, { imageAlt: e.target.value })}
                  />
                ) : null}
              </div>

              <div className="question-code-editor">
                <select
                  value={question.codeLanguage}
                  onChange={(e) => updateQuestion(qIndex, { codeLanguage: e.target.value })}
                >
                  {CODE_LANGUAGES.map((item) => (
                    <option key={item.value || "none"} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <textarea
                  rows={6}
                  placeholder="Optional code/program for this question"
                  value={question.codeSnippet}
                  onChange={(e) => updateQuestion(qIndex, { codeSnippet: e.target.value })}
                />
              </div>

              <div className="admin-options-grid">
                {question.options.map((opt, oIndex) => (
                  <div key={`q-${qIndex}-o-${oIndex}`} className="admin-option-row">
                    <input
                      type="radio"
                      name={`correct-${qIndex}`}
                      checked={question.correctAnswer === opt && opt !== ""}
                      onChange={() => updateQuestion(qIndex, { correctAnswer: opt })}
                    />
                    <input
                      type="text"
                      placeholder={`Option ${oIndex + 1}`}
                      value={opt}
                      onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {success ? <div className="admin-state">{success}</div> : null}
        {error ? <div className="admin-state admin-error">{error}</div> : null}

        <div className="actions-row">
          <button type="submit" disabled={saving}>{saving ? "Creating..." : "Create Exam"}</button>
          <button type="button" className="secondary" onClick={resetForm}>
            Reset
          </button>
        </div>
      </form>
    </section>
  );
};

export default AdminQuizzes;
